import { collection, getDocs, query, where } from 'firebase/firestore';
import { BarChart2, ArrowLeft, Download } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, Col, Form, Row, Spinner } from 'react-bootstrap';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { pdf } from '@react-pdf/renderer';
import { useNavigate } from 'react-router-dom';
import { db } from '../../firebase/config';
import { getReservaById } from '../../services/firestoreService';
import { getFuncionarios } from '../../services/funcionarioService';
import { Atendimento, Funcionario, Reserva } from '../../types';
import { useAuth } from '../../hooks/useAuth';
import { getUserProfile } from '../../services/userService';
import MainHeader from '../Common/MainHeader/MainHeader';
import RelatorioFuncionariosPDF, { RelatorioItem } from './RelatorioFuncionariosPDF';

const RelatorioFuncionarios: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [funcionarios, setFuncionarios] = useState<Funcionario[]>([]);
  const [selectedFuncionarioId, setSelectedFuncionarioId] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<Date | null>(null);
  const [dataFim, setDataFim] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [empresaNome, setEmpresaNome] = useState<string>('');
  const [logoUrl, setLogoUrl] = useState<string>('');

  useEffect(() => {
    if (user?.uid) {
      getFuncionarios(user.uid).then(res => {
        if (res.success && res.data) setFuncionarios(res.data);
      });
      getUserProfile().then(res => {
        if (res.success && res.data) {
          setEmpresaNome(res.data.nome || '');
          setLogoUrl(res.data.contractLogoURL || '');
        }
      });
    }
  }, [user?.uid]);

  const handleDownload = async () => {
    if (!dataInicio || !dataFim) {
      setError('Selecione o período completo');
      return;
    }
    if (!user?.uid) return;

    setError('');
    setLoading(true);

    try {
      const dataInicioStr = dataInicio.toISOString().split('T')[0];
      const dataFimStr = dataFim.toISOString().split('T')[0];

      const constraints: any[] = [
        where('userId', '==', user.uid),
        where('dataAtendimento', '>=', dataInicioStr),
        where('dataAtendimento', '<=', dataFimStr),
      ];

      if (selectedFuncionarioId) {
        constraints.push(where('funcionarioId', '==', selectedFuncionarioId));
      }

      const snapshot = await getDocs(query(collection(db, 'atendimentos'), ...constraints));

      const atendimentos: Atendimento[] = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate(),
        updatedAt: d.data().updatedAt?.toDate(),
      } as Atendimento));

      atendimentos.sort((a, b) => {
        const dc = a.dataAtendimento.localeCompare(b.dataAtendimento);
        if (dc !== 0) return dc;
        return a.horaInicio.localeCompare(b.horaInicio);
      });

      // Buscar numeroReserva dos atendimentos vinculados
      const atendimentoReservaIds = new Set(atendimentos.filter(a => a.reservaId).map(a => a.reservaId!));
      const reservaMap: Record<string, string> = {};

      await Promise.all(
        Array.from(atendimentoReservaIds).map(async (rid) => {
          const res = await getReservaById(rid);
          if (res.success && res.data?.numeroReserva) {
            reservaMap[rid] = res.data.numeroReserva;
          }
        })
      );

      // Buscar reservas criadas diretamente com atendente (sem atendimento)
      const reservasSnap = await getDocs(query(
        collection(db, 'reservas'),
        where('userId', '==', user.uid),
        where('funcionarioId', '>', '')
      ));

      const reservasDiretas = reservasSnap.docs
        .map(d => ({ id: d.id, ...d.data(), createdAt: d.data().createdAt?.toDate() } as Reserva))
        .filter(r => {
          // Ignorar se já aparece via atendimento
          if (atendimentoReservaIds.has(r.id!)) return false;
          // Filtrar por funcionário se selecionado
          if (selectedFuncionarioId && r.funcionarioId !== selectedFuncionarioId) return false;
          // Filtrar pela data de criação da reserva (produtividade)
          const dataCriacao = r.createdAt
            ? `${(r.createdAt as Date).getFullYear()}-${String((r.createdAt as Date).getMonth() + 1).padStart(2, '0')}-${String((r.createdAt as Date).getDate()).padStart(2, '0')}`
            : (r.dataRetirada ? r.dataRetirada.split('T')[0] : '');
          return dataCriacao >= dataInicioStr && dataCriacao <= dataFimStr;
        });

      const itemsAtendimentos: RelatorioItem[] = atendimentos.map(a => ({
        funcionarioNome: a.funcionarioNome,
        clienteNome: a.clienteNome,
        dataAtendimento: a.dataAtendimento,
        horaInicio: a.horaInicio,
        horaFim: a.horaFim,
        duracaoMinutos: a.duracaoMinutos,
        numeroReserva: a.reservaId ? reservaMap[a.reservaId] : undefined,
        resultado: a.resultado,
      }));

      const itemsReservasDiretas: RelatorioItem[] = reservasDiretas.map(r => ({
        funcionarioNome: r.funcionarioNome || '',
        clienteNome: r.clienteNome || '',
        dataAtendimento: r.createdAt
          ? `${(r.createdAt as Date).getFullYear()}-${String((r.createdAt as Date).getMonth() + 1).padStart(2, '0')}-${String((r.createdAt as Date).getDate()).padStart(2, '0')}`
          : (r.dataRetirada ? r.dataRetirada.split('T')[0] : ''),
        horaInicio: r.createdAt ? (r.createdAt as Date).toTimeString().split(' ')[0] : undefined,
        horaFim: r.horaFinalizacao,
        duracaoMinutos: undefined,
        numeroReserva: r.numeroReserva,
        resultado: 'converteu_reserva',
      }));

      const items: RelatorioItem[] = [...itemsAtendimentos, ...itemsReservasDiretas].sort((a, b) =>
        a.dataAtendimento.localeCompare(b.dataAtendimento)
      );

      const funcionarioFiltroNome = selectedFuncionarioId
        ? funcionarios.find(f => f.id === selectedFuncionarioId)?.nome || 'Todos'
        : 'Todos';

      const blob = await pdf(
        <RelatorioFuncionariosPDF
          items={items}
          dataInicio={dataInicioStr}
          dataFim={dataFimStr}
          funcionarioFiltro={funcionarioFiltroNome}
          empresaNome={empresaNome}
          logoUrl={logoUrl}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `relatorio-produtividade-${dataInicioStr}-${dataFimStr}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      setError('Erro ao gerar relatório: ' + (err.message || 'Erro desconhecido'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <MainHeader
        title="Relatório de Produtividade"
        subtitle="Selecione o período e o funcionário para baixar o relatório"
        icon={<BarChart2 size={32} />}
      >
      </MainHeader>

      <Button
        onClick={() => navigate('/funcionarios')}
        className="d-flex align-items-center gap-2 mb-3"
        style={{ border: '2px solid#2a5298', backgroundColor: '#fff', color: '#2a5298', }}
      >
        <ArrowLeft size={20} />
        Voltar
      </Button>
      <Card style={{ maxWidth: 700 }}>
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={5}>
              <Form.Group>
                <Form.Label>Data Início *</Form.Label>
                <DatePicker
                  selected={dataInicio}
                  onChange={setDataInicio}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="Data início"
                  locale="pt-BR"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                />
              </Form.Group>
            </Col>
            <Col md={5}>
              <Form.Group>
                <Form.Label>Data Fim *</Form.Label>
                <DatePicker
                  selected={dataFim}
                  onChange={setDataFim}
                  dateFormat="dd/MM/yyyy"
                  className="form-control"
                  placeholderText="Data fim"
                  locale="pt-BR"
                  showMonthDropdown
                  showYearDropdown
                  dropdownMode="select"
                  minDate={dataInicio || undefined}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 align-items-end mt-1">
            <Col md={10}>
              <Form.Group>
                <Form.Label>Funcionário</Form.Label>
                <Form.Select
                  value={selectedFuncionarioId}
                  onChange={e => setSelectedFuncionarioId(e.target.value)}
                >
                  <option value="">Todos os funcionários</option>
                  {funcionarios.map(f => (
                    <option key={f.id} value={f.id}>{f.nome}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {error && <Alert variant="danger" className="mt-3 mb-0">{error}</Alert>}

          <div className="mt-4">
            <Button
              variant="success"
              onClick={handleDownload}
              disabled={loading}
              className="d-flex align-items-center gap-2"
              style={{ borderRadius: '10px', padding: '10px 28px' }}
            >
              {loading
                ? <><Spinner size="sm" animation="border" /> Gerando...</>
                : <><Download size={18} /> Baixar Relatório</>
              }
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default RelatorioFuncionarios;
